/**
  {
    "action": null,
    "contact_id": "8ff2e1f4-422a-4713-b414-5c4092e6fc3a",
    "created_at": "2017-09-25T11:48:47.121-05:00",
    "conversation_id": "5b163ed8-d642-484f-8b44-27ef0dfb7a98",
    "dial_call_status": "no-answer",
    "direction": "outgoing",
    "district_id": "5126918ae9c77f9384000053",
    "id": "0882eb7c-73b9-4195-bd40-754d6a6fe1dd",
    "recording_duration": 0,
    "recording_path": null,
    "recording_sid": null,
    "school_id": "51269191e9c77f9384000455",
    "student_id": "51db4bd8e9c77f81290001ec",
    "user_id": "adf387de-51f6-4b57-9250-1b4b9b120b59",
    "notes": [],
    "voicemails": [],
    "call_transcripts":[{}]
  }
*/

import { observable, action, computed, autorun } from 'mobx'

import { setter }                                from 'mobx-decorators'
import uuid                                      from 'uuid'
import xhr                                       from 'helpers/XHR'
import DateFormat                                from 'helpers/DateFormat'
import getError                                  from 'helpers/ErrorParser'
import uiStore                                   from 'stores/UiStore'
import _                                         from 'lodash'
import Transcript                                from 'stores/models/Transcript'
import CallNote                                  from 'stores/models/CallNote'

export default class Call {
  callStore = null
  id = null
  createdAt = null
  contact = null
  user = null
  direction = null
  _transcript = new Transcript()

  @setter @observable action = null
  @setter @observable recordingPath = null
  @setter @observable recordingDuration = 0
  @setter @observable callStatus = null
  @setter @observable isRead = true
  @setter @observable isLoading = false
  @setter @observable isTranslating = false
  @setter @observable isError = false
  @setter @observable showNoteModal = false
  @setter @observable noteText = ''
  @setter @observable isRequestingTranscript = false
  @observable callNotes = observable.map()
  @observable voicemails = []

  constructor(store, json) {
    this.callStore = store
    this.update(json)
    this.setVoicemail()

    this.initAutoruns()
  }

  // Computed
  @computed get isMissedCall() {
    return !this.isVoicemail && this.callStatus !== 'completed'
  }

  @computed get isVoicemail() {
    return this.action === 'voicemail'
  }

  @computed get isIncoming() {
    return this.direction === 'incoming'
  }

  @computed get fullDate() {
    return `${DateFormat.fullDate(
      this.createdAt
    )} at ${DateFormat.time12Hour(this.createdAt)}`
  }

  @computed get userName() {
    return `${this.user.first_name} ${this.user.last_name}`
  }

  @computed get contactName() {
    return this.contact.name
  }

  @computed get studentName() {
    return this.contact.student.full_name
  }

  @computed get isSelected() {
    return this.callStore.selectedCall === this
  }

  @computed get transcript() {
    return this._transcript.transcript
  }

  @computed get orderedNotes() {
    if (!this.callNotes.size) return null

    return _.orderBy(this.callNotes.values(), 'createdAt', 'desc')
  }

  @computed get isFetchingTranscript() {
    return this._transcript.isFetchingTranscript
  }

  @computed get originalTranscript() {
    return this._transcript.originalTranscript
  }

  @computed get isVoicemailListened() {
    if (this.action === 'voicemail' && this.voicemails[0]) {
      return this.voicemails[0].listened
    }

    return false
  }

  @computed get hasNewNote() {
    return _.find(this.callNotes.values(), { isNew: true })
  }

  // Autoruns
  initAutoruns = () => {
    this.autoErrorNotifier()
  }

  autoErrorNotifier = () => {
    this.autoErrorDisposer = autorun('Watch errors', () => {
      if (this.isError && !this.isError.hideNotification) {
        uiStore.addNotification({
          title:   this.isError.title,
          message: this.isError.message,
          type:    'error'
        })
      }
    })
  }

  // Actions
  @action markAsRead = () => {
    this.isRead = true
  }

  @action update = ({
    id,
    action,
    created_at: createdAt,
    dial_call_status: callStatus,
    contact,
    user,
    recording_duration: recordingDuration,
    recording_path: recordingPath,
    direction,
    call_transcripts,
    voicemails = [],
    notes = [],
    student_id: studentId,
    conversation_id: conversationId
  }) => {
    this.id = id
    this.action = action
    this.createdAt = createdAt
    this.contact = contact
    this.user = user
    this.recordingDuration = recordingDuration
    this.recordingPath = recordingPath
    this.direction = direction
    this.callStatus = callStatus
    this.voicemails = voicemails
    this.studentId = studentId
    this.relationship = contact.relationship
    this.conversationId = conversationId

    this._transcript.setIsVoicemail(action === 'voicemail')
    this._transcript.updateFromJSON({ call_transcripts })
    notes.forEach((n) => {
      this.callNotes.set(n.id, new CallNote(n, this))
    })
  }

  @action handleSelect = () => {
    this.callStore.setSelectedCall(this)
    this._transcript.fetchCallTranscript(this.id)
    uiStore.setShowCallInfo(true)
  }

  @action setVoicemail = () => {
    if (!this.isVoicemail || _.isEmpty(this.voicemails)) return

    const {
      recording_url = '',
      duration = 0,
      transcript = ''
    } = this.voicemails[0]

    this.setRecordingPath(recording_url)
    this.setRecordingDuration(duration)
    this._transcript.setVoiceTranscript(transcript)
  }

  @action setVoicemailListened = () => {
    if (this.action === 'voicemail') {
      this.voicemails[0].listened = true
    }
  }

  @action handleOnTranslate = (isError, translatedText) => {
    !isError && this._transcript.translateTranscript(translatedText)
  }

  @action handleOnTranslating = (isTranslating) => {
    this.setIsTranslating(isTranslating)
  }

  @action dispose = () => {
    this._transcript.dispose()
  }

  @action initNewNote = () => {
    const id = uuid()

    this.callNotes.set(id, new CallNote({ isNew: true, id }, this))
  }

  @action fetchTranscript = async() => {
    try {
      this.setIsRequestingTranscript(true)

      const { data } = await xhr.post(
        `/commo/call_log/request_transcript/${this.id}`
      )

      this._transcript.callTranscript = data
    } catch (err) {
      this.setIsError(getError(err))
    } finally {
      this.setIsRequestingTranscript(false)
    }
  }
}
